
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  FaUserGraduate,
  FaClipboardList,
  FaLock,
  FaPhone,
  FaIdCard,
  FaBookOpen,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaVenusMars,
  FaCheckCircle,
  FaSpinner,
  FaGraduationCap
} from "react-icons/fa";

import api from "../services/api";
import { toast } from "react-toastify";
import CaixaModal from "../components/CaixaModal";

/* ================================
   ESTILIZAÇÃO
================================ */

const Container = styled.div`
  width: 100%;
  max-width: 1050px;
  margin: 0 auto;
  padding: 20px 16px 40px;
  font-family: "Segoe UI", sans-serif;

  @media(max-width: 600px) {
    padding: 12px 8px 30px;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme?.mode === "dark" ? "#171717" : "#fff"};
  border: 1px solid ${({ theme }) =>
    theme?.mode === "dark" ? "#303030" : "#e9e9e9"};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0,0,0,0.09);
`;

const Header = styled.div`
  background: linear-gradient(120deg, #420000, #850000, #c70303);
  padding: 30px 35px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media(max-width: 600px) {
    padding: 23px 18px;
    align-items: flex-start;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 17px;
`;

const HeaderIcon = styled.div`
  width: 55px;
  height: 55px;
  flex-shrink: 0;
  border-radius: 15px;
  background: rgba(255,255,255,0.13);
  display: grid;
  place-items: center;
  font-size: 25px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 750;
  letter-spacing: -0.5px;

  @media(max-width: 500px) {
    font-size: 19px;
  }
`;

const Subtitle = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: rgba(255,255,255,0.75);
`;

const HeaderBadge = styled.div`
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 30px;
  padding: 9px 13px;
  font-size: 12px;
  white-space: nowrap;

  @media(max-width: 600px) {
    display: none;
  }
`;

const Body = styled.div`
  padding: 30px 35px;

  @media(max-width: 600px) {
    padding: 22px 17px;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) =>
    theme?.mode === "dark" ? "#fff" : "#292929"};
  font-size: 15px;
  font-weight: 750;
  margin-bottom: 20px;

  svg {
    color: #c70303;
    font-size: 17px;
  }
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 21px 20px;

  @media(max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 17px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  &.full {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 650;
  color: ${({ theme }) =>
    theme?.mode === "dark" ? "#ddd" : "#444"};

  span {
    color: #c70303;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 14px;
    color: #999;
    font-size: 15px;
    pointer-events: none;
  }
`;

const Input = styled.input`
  width: 100%;
  height: 47px;
  border: 1px solid ${({ theme }) =>
    theme?.mode === "dark" ? "#3b3b3b" : "#dedede"};
  border-radius: 10px;
  padding: 0 14px 0 42px;
  background: ${({ theme }) =>
    theme?.mode === "dark" ? "#222" : "#fafafa"};
  color: ${({ theme }) =>
    theme?.mode === "dark" ? "#fff" : "#333"};
  font-size: 14px;
  outline: none;
  transition: .2s;

  &:focus {
    border-color: #c70303;
    background: ${({ theme }) =>
      theme?.mode === "dark" ? "#282020" : "#fff"};
    box-shadow: 0 0 0 3px rgba(199,3,3,.08);
  }

  &::placeholder {
    color: #aaa;
  }

  &:disabled {
    opacity: .6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  height: 47px;
  border: 1px solid ${({ theme }) =>
    theme?.mode === "dark" ? "#3b3b3b" : "#dedede"};
  border-radius: 10px;
  padding: 0 14px 0 42px;
  background: ${({ theme }) =>
    theme?.mode === "dark" ? "#222" : "#fafafa"};
  color: ${({ theme }) =>
    theme?.mode === "dark" ? "#fff" : "#333"};
  font-size: 14px;
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: #c70303;
    box-shadow: 0 0 0 3px rgba(199,3,3,.08);
  }

  option {
    background: #fff;
    color: #222;
  }

  &:disabled {
    opacity: .6;
    cursor: not-allowed;
  }
`;

const InfoGrid = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;

  @media(max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 17px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme?.mode === "dark" ? "#222" : "#f8f8f8"};
  border: 1px solid ${({ theme }) =>
    theme?.mode === "dark" ? "#383838" : "#ededed"};
`;

const InfoIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: ${({ $blue }) =>
    $blue ? "rgba(37,99,235,.10)" : "rgba(199,3,3,.09)"};
  color: ${({ $blue }) => $blue ? "#3b82f6" : "#c70303"};
  display: grid;
  place-items: center;
  font-size: 18px;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  min-width: 0;

  small {
    display: block;
    color: ${({ theme }) =>
      theme?.mode === "dark" ? "#aaa" : "#777"};
    font-size: 11px;
    margin-bottom: 5px;
  }

  strong {
    display: block;
    font-size: 16px;
    color: ${({ theme }) =>
      theme?.mode === "dark" ? "#fff" : "#292929"};
    overflow-wrap: anywhere;
  }
`;

const Divider = styled.div`
  grid-column: 1 / -1;
  height: 1px;
  background: ${({ theme }) =>
    theme?.mode === "dark" ? "#333" : "#eee"};
  margin: 5px 0;
`;

const SubmitButton = styled.button`
  grid-column: 1 / -1;
  height: 51px;
  border: none;
  border-radius: 11px;
  background: linear-gradient(120deg, #c70303, #650000);
  color: white;
  font-size: 14px;
  font-weight: 750;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: .25s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(199,3,3,.2);
  }

  &:disabled {
    opacity: .65;
    cursor: not-allowed;
  }
`;

const CaixaButton = styled.button`
  border: 1px solid rgba(255,255,255,.3);
  background: rgba(255,255,255,.12);
  color: white;
  border-radius: 9px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 650;
  font-size: 12px;
  cursor: pointer;
  transition: .2s;

  &:hover {
    background: rgba(255,255,255,.22);
  }
`;

const Message = styled.div`
  grid-column: 1 / -1;
  background: rgba(199,3,3,.07);
  color: #c70303;
  border: 1px solid rgba(199,3,3,.15);
  border-radius: 10px;
  padding: 12px;
  font-size: 13px;
`;

const FooterNote = styled.div`
  margin-top: 22px;
  padding-top: 17px;
  border-top: 1px solid ${({ theme }) =>
    theme?.mode === "dark" ? "#333" : "#eee"};
  color: ${({ theme }) =>
    theme?.mode === "dark" ? "#999" : "#888"};
  font-size: 11px;
  text-align: center;
`;

const LoadingText = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) =>
    theme?.mode === "dark" ? "#ccc" : "#777"};
  font-size: 13px;
`;

/* ================================
   COMPONENTE PRINCIPAL
================================ */

export default function InscricaoPage() {

  const [niveis, setNiveis] = useState([]);
  const [valorMensalidade, setValorMensalidade] = useState(0);

  const [loading, setLoading] = useState(false);
  const [loadingNiveis, setLoadingNiveis] = useState(true);

  const [mensagem, setMensagem] = useState("");

  const [caixaAberto, setCaixaAberto] = useState(false);
  const [showCaixaModal, setShowCaixaModal] = useState(false);
  const [modoCaixa, setModoCaixa] = useState("abrir");

  const [form, setForm] = useState({
    nome: "",
    sexo: "M",
    telefone: "",
    nr_bi: "",
    nivel_id: "",
    valor_total: "",
    data_inscricao: ""
  });

  /* ================================
     CARREGAMENTO INICIAL
  ================================= */

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    await Promise.all([
      loadNiveis(),
      verificarCaixa()
    ]);
  };

  /* NÍVEIS */

  const loadNiveis = async () => {
    try {
      setLoadingNiveis(true);

      const res = await api.get("/niveis");

      setNiveis(Array.isArray(res.data) ? res.data : []);

    } catch (error) {
      toast.error("Não foi possível carregar os níveis.");
    } finally {
      setLoadingNiveis(false);
    }
  };

  /* CAIXA */

  const verificarCaixa = async () => {
    try {
      const res = await api.get("/caixa/status");
      setCaixaAberto(Boolean(res.data.aberto));
    } catch (error) {
      setCaixaAberto(false);
    }
  };

  /* ================================
     NÍVEL SELECIONADO
  ================================= */

  const nivelSelecionado = useMemo(() => {
    return niveis.find(
      n => Number(n.id) === Number(form.nivel_id)
    );
  }, [niveis, form.nivel_id]);

  useEffect(() => {
    setValorMensalidade(
      Number(nivelSelecionado?.preco_mensal || 0)
    );
  }, [nivelSelecionado]);

  /* MOEDA */

  const formatarMoeda = valor => {
    return Number(valor || 0).toLocaleString("pt-MZ", {
      style: "currency",
      currency: "MZN",
      minimumFractionDigits: 2
    });
  };

  /* ================================
     ALTERAÇÃO DOS CAMPOS
  ================================= */

  const handleChange = e => {
    const { name, value } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: value
    }));

    setMensagem("");
  };

  /* ================================
     SUBMISSÃO
  ================================= */

  const handleSubmit = async e => {
    e.preventDefault();

    if (loading) return;

    setMensagem("");

    if (!form.nivel_id) {
      setMensagem("Selecione o nível do aluno.");
      return;
    }

    if (!form.nome.trim()) {
      setMensagem("Informe o nome completo do aluno.");
      return;
    }

    try {

      // Verificar situação do caixa
      const status = await api.get("/caixa/status");

      setCaixaAberto(Boolean(status.data.aberto));

      if (!status.data.aberto) {
        setModoCaixa("abrir");
        setShowCaixaModal(true);
        return;
      }

      setLoading(true);

      const dados = {
        ...form,
        nome: form.nome.trim(),
        nivel_id: Number(form.nivel_id),
        valor_total: form.valor_total === ""
          ? null
          : Number(form.valor_total)
      };

      const res = await api.post("/inscricoes", dados);

      toast.success(
        `Inscrição realizada com sucesso! Nº Estudante: ${res.data.numero_estudante}`
      );

      // Limpar formulário
      setForm({
        nome: "",
        sexo: "M",
        telefone: "",
        nr_bi: "",
        nivel_id: "",
        valor_total: "",
        data_inscricao: ""
      });

      setValorMensalidade(0);

    } catch (error) {

      const erro =
        error.response?.data?.message ||
        "Não foi possível realizar a inscrição.";

      setMensagem(erro);
      toast.error(erro);

    } finally {
      setLoading(false);
    }
  };

  /* ================================
     FECHAR CAIXA
  ================================= */

  const fecharCaixa = () => {
    setModoCaixa("fechar");
    setShowCaixaModal(true);
  };

  const handleModalClose = () => {
    setShowCaixaModal(false);
    verificarCaixa();
  };

  /* ================================
     RENDER
  ================================= */

  return (
    <Container>

      <Card>

        <Header>

          <HeaderContent>

            <HeaderIcon>
              <FaUserGraduate />
            </HeaderIcon>

            <div>
              <Title>Inscrição de Alunos</Title>
              <Subtitle>
                Registo académico e atribuição de nível
              </Subtitle>
            </div>

          </HeaderContent>

          {caixaAberto && (
            <CaixaButton onClick={fecharCaixa}>
              <FaLock />
              Fechar Caixa
            </CaixaButton>
          )}

        </Header>

        <Body>

          <SectionTitle>
            <FaClipboardList />
            Dados da inscrição
          </SectionTitle>

          {loadingNiveis ? (

            <LoadingText>
              <FaSpinner className="fa-spin" /> Carregando níveis...
            </LoadingText>

          ) : (

            <Form onSubmit={handleSubmit}>

              {/* NOME */}

              <Field className="full">
                <Label>
                  Nome completo <span>*</span>
                </Label>

                <InputWrapper>
                  <FaUserGraduate />

                  <Input
                    type="text"
                    name="nome"
                    placeholder="Introduza o nome completo do aluno"
                    value={form.nome}
                    onChange={handleChange}
                    required
                    maxLength={150}
                    disabled={loading}
                  />
                </InputWrapper>
              </Field>

              {/* SEXO */}

              <Field>
                <Label>
                  Sexo <span>*</span>
                </Label>

                <InputWrapper>
                  <FaVenusMars />

                  <Select
                    name="sexo"
                    value={form.sexo}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </Select>
                </InputWrapper>
              </Field>

              {/* TELEFONE */}

              <Field>
                <Label>Telefone</Label>

                <InputWrapper>
                  <FaPhone />

                  <Input
                    type="tel"
                    name="telefone"
                    placeholder="Ex: 87 000 0000"
                    value={form.telefone}
                    onChange={handleChange}
                    maxLength={20}
                    disabled={loading}
                  />
                </InputWrapper>
              </Field>

              {/* BI */}

              <Field>
                <Label>Número do BI</Label>

                <InputWrapper>
                  <FaIdCard />

                  <Input
                    type="text"
                    name="nr_bi"
                    placeholder="Número de identificação"
                    value={form.nr_bi}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </InputWrapper>
              </Field>

              {/* NÍVEL */}

              <Field>
                <Label>
                  Nível do curso <span>*</span>
                </Label>

                <InputWrapper>
                  <FaBookOpen />

                  <Select
                    name="nivel_id"
                    value={form.nivel_id}
                    onChange={handleChange}
                    required
                    disabled={loading || niveis.length === 0}
                  >
                    <option value="">
                      Selecionar nível
                    </option>

                    {niveis.map(n => (
                      <option key={n.id} value={n.id}>
                        {n.nome} - {n.tipo}
                      </option>
                    ))}
                  </Select>
                </InputWrapper>
              </Field>

              {/* INFORMAÇÕES DO NÍVEL */}

              <InfoGrid>

                <InfoCard>
                  <InfoIcon>
                    <FaMoneyBillWave />
                  </InfoIcon>

                  <InfoContent>
                    <small>Mensalidade do nível</small>

                    <strong>
                      {formatarMoeda(valorMensalidade)}
                    </strong>
                  </InfoContent>
                </InfoCard>

                <InfoCard>
                  <InfoIcon $blue>
                    <FaGraduationCap />
                  </InfoIcon>

                  <InfoContent>
                    <small>Tipo de curso</small>

                    <strong>
                      {nivelSelecionado
                        ? nivelSelecionado.tipo
                        : "Não selecionado"}
                    </strong>
                  </InfoContent>
                </InfoCard>

              </InfoGrid>

              <Divider />

              {/* DATA */}

              <Field>
                <Label>Data da inscrição</Label>

                <InputWrapper>
                  <FaCalendarAlt />

                  <Input
                    type="date"
                    name="data_inscricao"
                    value={form.data_inscricao}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </InputWrapper>
              </Field>

              {/* VALOR */}

              <Field>
                <Label>Valor total da inscrição (MT)</Label>

                <InputWrapper>
                  <FaMoneyBillWave />

                  <Input
                    type="number"
                    name="valor_total"
                    placeholder="Introduza o valor"
                    value={form.valor_total}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    disabled={loading}
                  />
                </InputWrapper>
              </Field>

              {/* MENSAGEM */}

              {mensagem && (
                <Message>
                  {mensagem}
                </Message>
              )}

              {/* SUBMETER */}

              <SubmitButton
                type="submit"
                disabled={loading || loadingNiveis || niveis.length === 0}
              >
                {loading ? (
                  <>
                    <FaSpinner className="fa-spin" />
                    A processar inscrição...
                  </>
                ) : (
                  <>
                    <FaCheckCircle />
                    Realizar Inscrição
                  </>
                )}
              </SubmitButton>

            </Form>

          )}

          {!loadingNiveis && niveis.length === 0 && (
            <Message>
              Não existem níveis cadastrados. Cadastre primeiro um nível
              para poder realizar inscrições.
            </Message>
          )}

          <FooterNote>
            CAJ ELEMENTARY SCHOOL · Sistema de Gestão Académica
          </FooterNote>

        </Body>

      </Card>

      {/* MODAL DO CAIXA */}

      {showCaixaModal && (
        <CaixaModal
          modo={modoCaixa}
          onClose={handleModalClose}
        />
      )}

    </Container>
  );
}
